import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaBell, FaCheck, FaTimes, FaClock, FaListUl } from "react-icons/fa";
import { formatMoney, resolveCurrencyContext } from "../utils/currency";
import { saveNotifications } from "../utils/notificationHistory";
import { API_BASE as API } from "../config/constants";
const QR_ALERTS_ENABLED_KEY = "qr_order_alerts_enabled";
const QR_POLL_INTERVAL_MS = 15000;
const QR_POLL_MIN_GAP_MS = 10000;

const toToday = () => {
  const d = new Date();
  const p = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const normalizeAddonIds = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeAddonIds(parsed);
    } catch {
      // ignore
    }
    return value
      .split(",")
      .map((id) => Number(String(id).trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  return [];
};

const mapLineItem = (entry, idx) => {
  if (!entry) return null;
  const productId = Number(entry?.productid ?? entry?.product_id ?? entry?.id ?? 0);
  const quantity = Number(entry?.qty ?? entry?.quantity ?? 0);
  if (!Number.isFinite(productId) || productId <= 0 || !Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  const variantIdRaw = entry?.variantid ?? entry?.variant_id ?? entry?.variantId ?? null;
  const variantId = Number(variantIdRaw);
  const addonIds = normalizeAddonIds(entry?.addon_ids ?? entry?.addons_json ?? entry?.addons ?? entry?.addonIds);

  const baseName =
    entry?.name ||
    entry?.productname ||
    entry?.product_name ||
    entry?.product?.name ||
    `Item ${idx + 1}`;
  const variantLabel = String(
    entry?.variant_label || entry?.variantname || entry?.variant_name || entry?.variant?.label || ""
  ).trim();
  const addonLabel = String(entry?.addon_label || "").trim();
  const nameWithVariant = variantLabel && !String(baseName).includes(`(${variantLabel})`)
    ? `${baseName} (${variantLabel})`
    : String(baseName);

  return {
    lineKey: entry?.lineKey || `p${productId}-v${Number.isFinite(variantId) && variantId > 0 ? variantId : "base"}-a${addonIds.join("_") || "none"}`,
    productid: productId,
    variantid: Number.isFinite(variantId) && variantId > 0 ? variantId : null,
    addon_ids: addonIds,
    quantity,
    qty: quantity,
    price: Number(entry?.price ?? 0),
    priceaftertax: Number(entry?.priceaftertax ?? entry?.price_after_tax ?? entry?.price ?? 0),
    taxedprice: Number(entry?.taxedprice ?? entry?.priceaftertax ?? entry?.price ?? 0),
    taxamount: Number(entry?.taxamount ?? 0),
    sgst: Number(entry?.sgst ?? 0),
    cgst: Number(entry?.cgst ?? 0),
    addon_label: addonLabel,
    variant_label: variantLabel,
    name: addonLabel ? `${nameWithVariant} + ${addonLabel}` : nameWithVariant,
  };
};

const mapOrder = (entry, restaurantId) => {
  const orderId = String(entry?.qrorderid ?? entry?.id ?? entry?.order_id ?? "").trim();
  if (!orderId) return null;
  const items = Array.isArray(entry?.items)
    ? entry.items
    : Array.isArray(entry?.itemlist)
    ? entry.itemlist
    : [];
  const mappedItems = items.map((item, idx) => mapLineItem(item, idx)).filter(Boolean);
  const summary = mappedItems.length
    ? mappedItems
        .map((it) => {
          const qty = Number(it?.quantity ?? it?.qty ?? 0);
          return qty > 1 ? `${it.name} x${qty}` : it.name;
        })
        .slice(0, 3)
        .join(", ")
    : "";

  return {
    orderId,
    restaurantId,
    tableNo: entry?.table_no ?? entry?.tableid ?? entry?.table_id ?? "",
    customerName: String(entry?.name || entry?.customer_name || "").trim(),
    customerMobile: String(entry?.mobile || entry?.customer_mobile || "").trim(),
    orderNote: String(entry?.notes || entry?.customer_note || "").trim(),
    totalAmount: Number(entry?.totalamount ?? entry?.total_amount ?? 0),
    items: mappedItems,
    summary,
    raw: entry,
  };
};

const resolveOrderRouteTable = (value) => {
  const n = Number(value);
  if (Number.isFinite(n) && n === -1) return "TakeAway-1";
  if (Number.isFinite(n) && n > 0) return String(n);
  const text = String(value || "").trim();
  if (!text) return "TakeAway-1";
  if (text === "-1") return "TakeAway-1";
  if (/^takeaway/i.test(text)) return text.replace(/\s+/g, "-");
  return text;
};

const hasTableRunningOrderInLocal = (tableKey) => {
  if (!tableKey) return false;
  const key = String(tableKey);
  try {
    const itemsRaw = localStorage.getItem(`ordersitems1${key}`);
    const linesRaw = localStorage.getItem(`orderlines1${key}`);
    const amountRaw = localStorage.getItem(`orderAmount1${key}`);
    const items = itemsRaw ? JSON.parse(itemsRaw) : null;
    const lines = linesRaw ? JSON.parse(linesRaw) : null;
    const hasItems =
      items && typeof items === "object"
        ? Object.values(items).some((qty) => Number(qty) > 0)
        : false;
    const hasLines = Array.isArray(lines)
      ? lines.some((line) => Number(line?.quantity ?? line?.qty ?? 0) > 0)
      : false;
    const hasAmount = Number(amountRaw || 0) > 0;
    return hasItems || hasLines || hasAmount;
  } catch {
    return false;
  }
};

export default function GlobalQrOrderNotifier() {
  const navigate = useNavigate();
  const location = useLocation();
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const [processingId, setProcessingId] = useState("");
  const [mergeDecisionOrder, setMergeDecisionOrder] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    const stored = localStorage.getItem(QR_ALERTS_ENABLED_KEY);
    if (stored === null) return true;
    return stored === "true";
  });
  const dismissedSet = useMemo(() => new Set(dismissedIds), [dismissedIds]);
  const latestQueueIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pendingToneRef = useRef(false);
  const queueRef = useRef([]);
  const dismissedIdsRef = useRef([]);
  const alertsEnabledRef = useRef(true);
  const pendingFetchRef = useRef(null);
  const lastFetchAtRef = useRef(0);

  const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
  const restaurantId = user?.id;
  const fmtMoney = (value) => formatMoney(value, resolveCurrencyContext(user));
  const pollingBlocked =
    location.pathname === "/qr/management" || location.pathname === "/qr/table-order";

  const playAlertTone = useCallback(() => {
    try {
      if (typeof window === "undefined") return false;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return false;

      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContextClass();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        if (!audioUnlockedRef.current) {
          pendingToneRef.current = true;
          return false;
        }
        ctx.resume().catch(() => {});
      }

      const chime = (frequency, delaySec = 0, lengthSec = 0.24) => {
        const oscMain = ctx.createOscillator();
        const oscHarm = ctx.createOscillator();
        const gain = ctx.createGain();
        oscMain.type = "triangle";
        oscHarm.type = "sine";
        oscMain.frequency.setValueAtTime(frequency, ctx.currentTime + delaySec);
        oscHarm.frequency.setValueAtTime(frequency * 2, ctx.currentTime + delaySec);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + delaySec);
        gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + delaySec + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delaySec + lengthSec);
        oscMain.connect(gain);
        oscHarm.connect(gain);
        gain.connect(ctx.destination);
        oscMain.start(ctx.currentTime + delaySec);
        oscHarm.start(ctx.currentTime + delaySec);
        oscMain.stop(ctx.currentTime + delaySec + lengthSec + 0.02);
        oscHarm.stop(ctx.currentTime + delaySec + lengthSec + 0.02);
      };

      // clear and louder notification melody
      chime(880, 0.0, 0.22);
      chime(1174, 0.24, 0.22);
      chime(1568, 0.48, 0.26);
      chime(1174, 1.0, 0.18);
      chime(1568, 1.2, 0.22);
    } catch {
      // ignore audio errors
      return false;
    }

    try {
      if (navigator?.vibrate) navigator.vibrate([220, 120, 220]);
    } catch {
      // ignore vibration errors
    }
    return true;
  }, []);

  const fetchPending = useCallback(async ({ force = false } = {}) => {
    if (!restaurantId || pollingBlocked) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }

    const now = Date.now();
    if (!force) {
      if (pendingFetchRef.current) return pendingFetchRef.current;
      if (now - lastFetchAtRef.current < QR_POLL_MIN_GAP_MS) return;
    }

    const request = (async () => {
      lastFetchAtRef.current = Date.now();
    try {
      const today = toToday();
      const res = await fetch(`${API}/list/orderfromQR`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: restaurantId,
          fromdate: today,
          todate: today,
          type: -1,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return;

      const incoming = (Array.isArray(json?.list) ? json.list : [])
        .map((entry) => mapOrder(entry, restaurantId))
        .filter(Boolean);
      saveNotifications(incoming.map((item) => ({ id: `qr-${item.orderId}`, orderId: item.orderId, type: "QR Order", title: `New QR order #${item.orderId}`, message: `${item.customerName || "Customer"} · ${item.tableName || item.tableId || "Table"}`, payload: item })));

      setQueue((prev) => {
        const prevMap = new Map((prev || []).map((item) => [String(item.orderId), item]));
        const merged = incoming.map((item) => {
          const old = prevMap.get(String(item.orderId));
          return old ? { ...old, ...item } : item;
        });
        return merged;
      });

      const known = latestQueueIdsRef.current;
      const incomingIds = new Set(incoming.map((item) => String(item.orderId)));
      const hasNew = incoming.some((item) => !known.has(String(item.orderId)));
      latestQueueIdsRef.current = incomingIds;

      if (hasNew && incoming.length > 0) {
        if (alertsEnabledRef.current) {
          playAlertTone();
          toast.info(`New QR order received (${incoming.length} pending)`);
        }
      }
    } catch {
      // silent retry on next poll
    } finally {
      pendingFetchRef.current = null;
    }
    })();

    pendingFetchRef.current = request;
    return request;
  }, [restaurantId, pollingBlocked, playAlertTone]);

  useEffect(() => {
    if (!restaurantId || pollingBlocked) return undefined;
    fetchPending({ force: true });
    const t = setInterval(() => {
      fetchPending();
    }, QR_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [restaurantId, pollingBlocked, fetchPending]);

  useEffect(() => {
    if (!restaurantId || pollingBlocked) return undefined;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchPending({ force: true });
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [restaurantId, pollingBlocked, fetchPending]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const unlockAudio = async () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new AudioContextClass();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") {
          await ctx.resume();
        }
        audioUnlockedRef.current = true;
        if (pendingToneRef.current) {
          pendingToneRef.current = false;
          playAlertTone();
        }
      } catch {
        // browser can still block until next interaction
      }
    };

    window.addEventListener("pointerdown", unlockAudio, { passive: true });
    window.addEventListener("keydown", unlockAudio);
    window.addEventListener("touchstart", unlockAudio, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
  }, [playAlertTone]);

  useEffect(() => {
    const ids = new Set(queue.map((item) => String(item.orderId)));
    setDismissedIds((prev) => prev.filter((id) => ids.has(id)));
  }, [queue]);

  useEffect(() => {
    queueRef.current = Array.isArray(queue) ? queue : [];
  }, [queue]);

  useEffect(() => {
    dismissedIdsRef.current = Array.isArray(dismissedIds) ? dismissedIds : [];
  }, [dismissedIds]);

  useEffect(() => {
    alertsEnabledRef.current = alertsEnabled;
  }, [alertsEnabled]);

  useEffect(() => {
    const loadPref = () => {
      const stored = localStorage.getItem(QR_ALERTS_ENABLED_KEY);
      setAlertsEnabled(stored === null ? true : stored === "true");
    };
    loadPref();
    window.addEventListener("storage", loadPref);
    window.addEventListener("qr-alert-settings-changed", loadPref);
    return () => {
      window.removeEventListener("storage", loadPref);
      window.removeEventListener("qr-alert-settings-changed", loadPref);
    };
  }, []);

  useEffect(() => {
    const reminderInterval = setInterval(() => {
      if (!alertsEnabledRef.current) return;
      const dismissed = new Set(
        (dismissedIdsRef.current || []).map((id) => String(id))
      );
      const pending = (queueRef.current || []).filter(
        (item) => !dismissed.has(String(item?.orderId || ""))
      );
      if (!pending.length) return;
      playAlertTone();
      toast.warn(
        `Reminder: ${pending.length} QR order${pending.length > 1 ? "s" : ""} pending`
      );
    }, 180000); // every 3 minutes

    return () => clearInterval(reminderInterval);
  }, [playAlertTone]);

  const removeOrder = useCallback((orderId) => {
    const key = String(orderId || "");
    setQueue((prev) => prev.filter((item) => String(item?.orderId) !== key));
    setDismissedIds((prev) => prev.filter((id) => id !== key));
  }, []);

  const markSkip = useCallback((orderId) => {
    const key = String(orderId || "");
    if (!key) return;
    setDismissedIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setOpen(false);
  }, []);

  const finalizeAcceptNavigation = useCallback(
    ({ orderId, mappedOrder, routeTable, orderIdForEdit, qrMergeMode }) => {
      removeOrder(orderId);
      setOpen(false);
      setMergeDecisionOrder(null);
      toast.success("QR order accepted");

      navigate(`/order/${encodeURIComponent(routeTable)}`, {
        state: {
          orderForEdit: mappedOrder,
          order: mappedOrder,
          orderid: orderIdForEdit,
          qrorderid: orderId,
          openFromQrNotification: true,
          qrMergeMode,
        },
      });
    },
    [navigate, removeOrder]
  );

  const acceptQrOrder = useCallback(
    async (order, qrMergeMode) => {
      const orderId = String(order?.orderId || "");
      if (!orderId || processingId) return;

      setProcessingId(orderId);
      try {
        const res = await fetch(`${API}/acceptreject/qrorder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ qrorderid: orderId, accept: 1 }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result?.success === false) {
          throw new Error(result?.message || "Unable to process QR order");
        }

        const mappedOrder = {
          ...(order?.raw || {}),
          ...(result?.order || {}),
          items: Array.isArray(result?.itemslist)
            ? result.itemslist
            : Array.isArray(result?.items)
            ? result.items
            : order?.items || [],
          orderitems: Array.isArray(result?.itemslist)
            ? result.itemslist
            : Array.isArray(result?.items)
            ? result.items
            : order?.items || [],
          notes: String(result?.order?.notes || order?.orderNote || "").trim(),
        };
        const routeTable = resolveOrderRouteTable(
          result?.table_no ?? result?.order?.table_no ?? mappedOrder?.table_no ?? order?.tableNo
        );
        const orderIdForEdit =
          mappedOrder?.id || mappedOrder?.orderid || mappedOrder?.order_id || null;

        finalizeAcceptNavigation({
          orderId,
          mappedOrder,
          routeTable,
          orderIdForEdit,
          qrMergeMode,
        });
      } catch (e) {
        toast.error(e?.message || "Failed to process QR order");
      } finally {
        setProcessingId("");
      }
    },
    [finalizeAcceptNavigation, processingId]
  );

  const closeMergeDecisionModal = useCallback(() => {
    if (processingId) return;
    setMergeDecisionOrder(null);
  }, [processingId]);

  const acceptRejectQrOrder = useCallback(
    async (order, accept) => {
      const orderId = String(order?.orderId || "");
      if (!orderId || processingId) return;

      if (accept === 1) {
        const routeTable = resolveOrderRouteTable(order?.tableNo);
        const hasExisting = hasTableRunningOrderInLocal(routeTable);
        if (hasExisting) {
          setMergeDecisionOrder(order);
          return;
        }
        await acceptQrOrder(order, "replace");
        return;
      }

      setProcessingId(orderId);
      try {
        const res = await fetch(`${API}/acceptreject/qrorder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ qrorderid: orderId, accept }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok || result?.success === false) {
          throw new Error(result?.message || "Unable to process QR order");
        }
        removeOrder(orderId);
        setOpen(false);
        toast.error("QR order declined");
      } catch (e) {
        toast.error(e?.message || "Failed to process QR order");
      } finally {
        setProcessingId("");
      }
    },
    [acceptQrOrder, processingId, removeOrder]
  );

  const activeOrder = useMemo(() => {
    const visible = queue.filter((item) => !dismissedSet.has(String(item.orderId)));
    return visible[0] || null;
  }, [queue, dismissedSet]);

  if (!restaurantId) return null;
  if (!alertsEnabled && queue.length === 0) return null;

  return (
    <Wrap>
      <BellBtn type="button" onClick={() => setOpen((v) => !v)}>
        <FaBell />
        {queue.length > 0 ? <Count>{queue.length}</Count> : null}
      </BellBtn>

      {open ? (
        <Panel>
          <PanelHead>
            <strong>QR Orders</strong>
            <small>{queue.length} pending</small>
          </PanelHead>

          {queue.length === 0 ? (
            <Empty>No pending QR orders</Empty>
          ) : (
            <List>
              {queue.map((order) => {
                const isDismissed = dismissedSet.has(String(order.orderId));
                const isBusy = processingId === String(order.orderId);
                const tableText = resolveOrderRouteTable(order?.tableNo);
                return (
                  <OrderCard key={order.orderId} $dim={isDismissed}>
                    <Top>
                      <div>Table {tableText}</div>
                      <Amt>{fmtMoney(order.totalAmount)}</Amt>
                    </Top>
                    {!!order?.summary && <Summary>{order.summary}</Summary>}
                    {!!order?.orderNote && <Note>Note: {order.orderNote}</Note>}
                    <ActionRow>
                      <Btn
                        type="button"
                        $tone="skip"
                        disabled={isBusy}
                        onClick={() => markSkip(order.orderId)}
                        title="Hide for now"
                      >
                        <FaClock /> Skip
                      </Btn>
                      <Btn
                        type="button"
                        $tone="decline"
                        disabled={isBusy}
                        onClick={() => acceptRejectQrOrder(order, 2)}
                      >
                        <FaTimes /> Decline
                      </Btn>
                      <Btn
                        type="button"
                        $tone="accept"
                        disabled={isBusy}
                        onClick={() => acceptRejectQrOrder(order, 1)}
                      >
                        <FaCheck /> {isBusy ? "Saving" : "Accept"}
                      </Btn>
                    </ActionRow>
                  </OrderCard>
                );
              })}
            </List>
          )}
        </Panel>
      ) : activeOrder ? (
        <PeekCard onClick={() => setOpen(true)}>
          <FaListUl />
          <span>New QR order on Table {resolveOrderRouteTable(activeOrder?.tableNo)}</span>
        </PeekCard>
      ) : null}

      {mergeDecisionOrder ? (
        <DecisionOverlay onClick={closeMergeDecisionModal}>
          <DecisionModal onClick={(e) => e.stopPropagation()}>
            <h4>Existing order found on table {resolveOrderRouteTable(mergeDecisionOrder?.tableNo)}</h4>
            <p>Choose how QR order should be handled.</p>
            <DecisionActions>
              <DecisionBtn
                type="button"
                onClick={() => closeMergeDecisionModal()}
                disabled={processingId === String(mergeDecisionOrder?.orderId || "")}
              >
                Cancel
              </DecisionBtn>
              <DecisionBtn
                type="button"
                $tone="append"
                onClick={() => acceptQrOrder(mergeDecisionOrder, "append")}
                disabled={processingId === String(mergeDecisionOrder?.orderId || "")}
              >
                Add To Existing
              </DecisionBtn>
              <DecisionBtn
                type="button"
                $tone="replace"
                onClick={() => acceptQrOrder(mergeDecisionOrder, "replace")}
                disabled={processingId === String(mergeDecisionOrder?.orderId || "")}
              >
                {processingId === String(mergeDecisionOrder?.orderId || "") ? "Saving..." : "Replace Previous"}
              </DecisionBtn>
            </DecisionActions>
          </DecisionModal>
        </DecisionOverlay>
      ) : null}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
`;

const BellBtn = styled.button`
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(var(--app-primary-rgb), 0.32);
  position: relative;
  font-size: 18px;
`;

const Count = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  background: #dc2626;
  border: 2px solid #fff;
`;

const Panel = styled.div`
  width: min(420px, calc(100vw - 28px));
  max-height: min(72vh, 640px);
  overflow: auto;
  background: #fff;
  border: 1px solid #dbe7f3;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(2, 12, 33, 0.2);
`;

const PanelHead = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fbff;
  border-bottom: 1px solid #e5edf6;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  strong {
    font-size: 15px;
    color: #0f172a;
  }
  small {
    font-size: 12px;
    color: #475569;
    font-weight: 700;
  }
`;

const Empty = styled.div`
  padding: 20px;
  text-align: center;
  color: #64748b;
  font-weight: 600;
`;

const List = styled.div`
  padding: 10px;
  display: grid;
  gap: 10px;
`;

const OrderCard = styled.article`
  border: 1px solid #dce8f4;
  border-radius: 14px;
  padding: 10px;
  background: #fff;
  opacity: ${({ $dim }) => ($dim ? 0.65 : 1)};
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
`;

const Amt = styled.div`
  color: var(--app-primary);
`;

const Summary = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #334155;
`;

const Note = styled.div`
  margin-top: 6px;
  padding: 7px 8px;
  border-radius: 9px;
  font-size: 12px;
  color: #1e293b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const ActionRow = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const Btn = styled.button`
  border: 1px solid #cdd9e8;
  border-radius: 10px;
  background: ${({ $tone }) =>
    $tone === "accept"
      ? "#e7f7ef"
      : $tone === "decline"
      ? "#feecec"
      : "#f8fafc"};
  color: ${({ $tone }) =>
    $tone === "accept"
      ? "#116149"
      : $tone === "decline"
      ? "#b42318"
      : "#334155"};
  font-size: 12px;
  font-weight: 700;
  padding: 8px 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PeekCard = styled.button`
  border: 1px solid #d7e8f8;
  background: #fff;
  color: #0f172a;
  border-radius: 999px;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.16);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const DecisionOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.5);
  display: grid;
  place-items: center;
  z-index: 1300;
  padding: 16px;
`;

const DecisionModal = styled.div`
  width: min(460px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #dce8f4;
  border-radius: 16px;
  box-shadow: 0 26px 46px rgba(15, 23, 42, 0.28);
  padding: 16px;
  h4 {
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 800;
  }
  p {
    margin: 8px 0 0;
    color: #475569;
    font-size: 14px;
    font-weight: 500;
  }
`;

const DecisionActions = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const DecisionBtn = styled.button`
  border: 1px solid
    ${({ $tone }) =>
      $tone === "replace" ? "#f2b8b5" : $tone === "append" ? "#9ecdf2" : "#cbd5e1"};
  border-radius: 10px;
  background: ${({ $tone }) =>
    $tone === "replace" ? "#fff2f2" : $tone === "append" ? "#ecf6ff" : "#f8fafc"};
  color: ${({ $tone }) =>
    $tone === "replace" ? "#b42318" : $tone === "append" ? "var(--app-primary)" : "#334155"};
  font-size: 13px;
  font-weight: 700;
  padding: 10px 8px;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
